import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Code,
  Flex,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Button,
} from "@chakra-ui/react";
import { DarwinAgent, PromptProto } from "build-ts/darwin_agent";
import { DarwinHeader, darwinAgentTypeFromJSON, reasoningEffortLevelToJSON } from "build-ts/common";
import { ReasoningEffortLevel, reasoningEffortLevelFromJSON } from "build-ts/common";
import {
  getAssignedPrompts,
  getPromptByid,
  updatePrompt,
  deletePrompt,
  duplicatePrompt,
  validateTemplateSyntax
} from "services/NewDarwinEvaServices";
import { useUser } from "context/UserContext";
import showToast from "hooks/useCustomToast";
import EvaAgentRightPanel from "./EvaAgentRightPanel";
import ConfirmationModal from "../common/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import {
  extractVariablesFromPrompt,
  convertVariablesToSampleData,
  formatValidationResult,
  hasTemplateVariables
} from "utils/templateValidation";

interface EvaAgentRightPanelWrapperProps {
  agent: DarwinAgent;
  promptConfig?: PromptProto;
  promptEditor: string;
  onPromptEditorChange: (value: string) => void;
  onPromptConfigChange?: (field: string, value: any) => void;
  analysis_flag?: string;
  isCreateMode?: boolean;
  onViewDocs?: () => void;
}

const EvaAgentRightPanelWrapper: React.FC<EvaAgentRightPanelWrapperProps> = ({
  agent,
  promptConfig,
  promptEditor,
  onPromptEditorChange,
  onPromptConfigChange,
  analysis_flag = "",
  isCreateMode = false,
  onViewDocs,
}) => {
  const { defaultTimeZone } = useUser();
  const navigate = useNavigate();

  // All state management
  const [testResult, setTestResult] = useState<string | null>(null);
  const [isValidationSuccess, setIsValidationSuccess] = useState<boolean>(true);
  const [variableNames, setVariableNames] = useState<string[]>([]);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [temperatureError, setTemperatureError] = useState<string>("");

  // Prompt management states
  const [isPromptEditing, setIsPromptEditing] = useState(false);
  const [assignedPrompts, setAssignedPrompts] = useState<DarwinHeader[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string>(promptConfig?.header?.id || "");
  const [currentPrompt, setCurrentPrompt] = useState<PromptProto | undefined>(promptConfig || (agent?.prompts && agent.prompts.length > 0 ? agent.prompts[0] : undefined));

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDuplicateConfirmation, setShowDuplicateConfirmation] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Refs to track operations that should skip auto-selection
  const isUpdatingRef = useRef(false);
  const isDuplicatingRef = useRef(false);

  // Modal states
  const [isTestOpen, setIsTestOpen] = useState(false);
  const [isVarsOpen, setIsVarsOpen] = useState(false);

  // Check if temperature is valid for API calls
  const isTemperatureValid = React.useCallback(() => {
    const tempValue = currentPrompt?.model_config?.temperature;
    if (tempValue === undefined || tempValue === null) return false;
    return tempValue >= 0 && tempValue <= 2;
  }, [currentPrompt?.model_config?.temperature]);

  // Load assigned prompts for the agent (only for dropdown, not for initial data)
  const loadAssignedPrompts = React.useCallback(async () => {
    try {
      setIsLoadingPrompts(true);
      const prompts = await getAssignedPrompts(agent.header?.id || "");
      setAssignedPrompts(prompts || []);
    } catch (error) {
      console.error("Error loading assigned prompts:", error);
      showToast("Failed to load prompts", "", "error");
    } finally {
      setIsLoadingPrompts(false);
    }
  }, [agent.header?.id]);

  // Initialize prompt data from prompt config when it changes
  useEffect(() => {
    if (promptConfig && !isCreateMode && promptConfig.header?.id !== selectedPromptId) {
      // Initialize form state from API data
      const initializedPrompt = {
        header: {
          id: promptConfig.header?.id || "",
          name: promptConfig.header?.name || "",
          description: promptConfig.header?.description || "",
          is_active: promptConfig.header?.is_active || false,
          created_date: promptConfig.header?.created_date || 0,
          last_modified_date: promptConfig.header?.last_modified_date || 0,
        },
        model_config: {
          model_name: promptConfig.model_config?.model_name || "gpt-4o",
          model_provider: promptConfig.model_config?.model_provider || "openai",
          temperature: promptConfig.model_config?.temperature || 0,
          reasoning_effort_level: promptConfig.model_config?.reasoning_effort_level || ReasoningEffortLevel.REASONING_NOT_VALID,
        },
        template: promptConfig.template || "",
      };
      setCurrentPrompt(initializedPrompt);
      setSelectedPromptId(promptConfig.header?.id || "");

      // Update prompt editor with current prompt template
      onPromptEditorChange(promptConfig.template || "");

      // Update model configuration from current prompt
      if (onPromptConfigChange) {
        onPromptConfigChange("header.id", promptConfig.header?.id || "");
        onPromptConfigChange("header.name", promptConfig.header?.name || "");
        onPromptConfigChange("template", promptConfig.template || "");
        onPromptConfigChange("model_config.model_name", promptConfig.model_config?.model_name || "gpt-4o");
        onPromptConfigChange("model_config.model_provider", promptConfig.model_config?.model_provider || "openai");
        onPromptConfigChange("model_config.temperature", promptConfig.model_config?.temperature || 0);
        onPromptConfigChange("model_config.reasoning_effort_level", reasoningEffortLevelToJSON(promptConfig.model_config?.reasoning_effort_level || ReasoningEffortLevel.REASONING_NOT_VALID));
        onPromptConfigChange("header.is_active", promptConfig.header?.is_active || false);
        onPromptConfigChange("header.description", promptConfig.header?.description || "");
        onPromptConfigChange("header.created_date", promptConfig.header?.created_date || "");
        onPromptConfigChange("header.last_modified_date", promptConfig.header?.last_modified_date || "");
      }
    }
  }, [promptConfig, isCreateMode, selectedPromptId, onPromptEditorChange, onPromptConfigChange]);

  // Load assigned prompts when component mounts
  useEffect(() => {
    if (!isCreateMode) {
      loadAssignedPrompts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, loadAssignedPrompts]);

  // Auto-select active prompt when prompts are loaded
  useEffect(() => {
    // Skip auto-selection during duplicate and update operations (use refs for reliable tracking)
    if (isDuplicatingRef.current || isUpdatingRef.current) {
      return;
    }
    if (!isCreateMode && assignedPrompts.length > 0) {
      const activePrompt = assignedPrompts.find((p: DarwinHeader) => p.is_active);
      if (activePrompt?.id && (!selectedPromptId || selectedPromptId !== activePrompt.id)) {
        handlePromptSelection(activePrompt.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedPrompts, isCreateMode]);

  // Handle prompt selection change
  const handlePromptSelection = React.useCallback(async (promptId: string) => {
    if (!promptId || promptId === selectedPromptId) return;

    try {
      const promptData = await getPromptByid(promptId);
      if (promptData) {
        // Update form state from selected prompt
        const updatedPrompt = {
          header: {
            id: promptData.header?.id || "",
            name: promptData.header?.name || "",
            description: promptData.header?.description || "",
            is_active: promptData.header?.is_active || false,
            created_date: promptData.header?.created_date || 0,
            last_modified_date: promptData.header?.last_modified_date || 0,
          },
          model_config: {
            model_name: promptData.model_config?.model_name || "gpt-4o",
            model_provider: promptData.model_config?.model_provider || "openai",
            temperature: promptData.model_config?.temperature || 0,
            reasoning_effort_level: reasoningEffortLevelFromJSON(promptData.model_config?.reasoning_effort_level) ?? ReasoningEffortLevel.REASONING_NOT_VALID,
          },
          template: promptData.template || "",
        };

        // Update states
        setCurrentPrompt(updatedPrompt);
        setSelectedPromptId(promptId);

        onPromptEditorChange(promptData.template || "");

        // Update model configuration from selected prompt
        if (onPromptConfigChange) {
          onPromptConfigChange("header.id", promptData.header?.id || "");
          onPromptConfigChange("header.name", promptData.header?.name || "");
          onPromptConfigChange("template", promptData.template || "");
          onPromptConfigChange("model_config.model_name", promptData.model_config?.model_name || "gpt-4o");
          onPromptConfigChange("model_config.model_provider", promptData.model_config?.model_provider || "openai");
          onPromptConfigChange("model_config.temperature", promptData.model_config?.temperature || 0);
          onPromptConfigChange("model_config.reasoning_effort_level", updatedPrompt.model_config.reasoning_effort_level);
          onPromptConfigChange("header.is_active", promptData.header?.is_active || false);
          onPromptConfigChange("header.description", promptData.header?.description || "");
          onPromptConfigChange("header.created_date", promptData.header?.created_date || "");
          onPromptConfigChange("header.last_modified_date", promptData.header?.last_modified_date || "");
        }
      }
    } catch (error) {
      console.error("Error loading prompt:", error);
      showToast("Failed to load prompt", "", "error");
    }
  }, [selectedPromptId, onPromptEditorChange, onPromptConfigChange]);

  // Handle prompt update
  const handleUpdatePrompt = React.useCallback(async () => {
    if (!currentPrompt) return;

    try {
      setIsUpdating(true);
      isUpdatingRef.current = true;
      if (!isTemperatureValid()) {
        setTemperatureError("Temperature must be between 0 and 2");
        isUpdatingRef.current = false;
        return;
      }
      const updatedPrompt: PromptProto = {
        header: {
          id: currentPrompt.header?.id || "",
          name: currentPrompt.header?.name || "",
          description: currentPrompt.header?.description || "",
          is_active: currentPrompt.header?.is_active || false,
        },
        model_config: {
          model_name: currentPrompt.model_config?.model_name || "gpt-4o",
          model_provider: currentPrompt.model_config?.model_provider || "openai",
          temperature: currentPrompt.model_config?.temperature || 0,
          reasoning_effort_level: reasoningEffortLevelFromJSON(currentPrompt.model_config?.reasoning_effort_level),
        },
        template: currentPrompt.template?.trim() || "",
      };

      const response = await updatePrompt(currentPrompt.header?.id || "", updatedPrompt);

      // Update the current prompt with the response data (including new modified_date)
      if (response) {
        const updatedPromptFromAPI = {
          header: {
            id: response.header?.id || currentPrompt.header?.id || "",
            name: response.header?.name || currentPrompt.header?.name || "",
            description: response.header?.description || currentPrompt.header?.description || "",
            is_active: response.header?.is_active || currentPrompt.header?.is_active || false,
            created_date: response.header?.created_date || currentPrompt.header?.created_date || 0,
            last_modified_date: response.header?.last_modified_date || currentPrompt.header?.last_modified_date || 0,
          },
          model_config: {
            model_name: response.model_config?.model_name || currentPrompt.model_config?.model_name || "gpt-4o",
            model_provider: response.model_config?.model_provider || currentPrompt.model_config?.model_provider || "openai",
            temperature: response.model_config?.temperature || currentPrompt.model_config?.temperature || 0,
            reasoning_effort_level: response.model_config?.reasoning_effort_level || currentPrompt.model_config?.reasoning_effort_level || ReasoningEffortLevel.REASONING_NOT_VALID,
          },
          template: response.template || currentPrompt.template || "",
        };

        setCurrentPrompt(updatedPromptFromAPI);

        // Update parent component with new data
        if (onPromptConfigChange) {
          onPromptConfigChange("header.last_modified_date", response.header?.last_modified_date || 0);
        }
      }

      showToast("Prompt updated successfully", "", "success");
      setIsPromptEditing(false);
      await loadAssignedPrompts(); // Reload prompts
    } catch (error) {
      console.error("Error updating prompt:", error);
      showToast("Failed to update prompt", "", "error");
    } finally {
      setIsUpdating(false);
      // Clear ref after a small delay to ensure useEffect has checked it
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [currentPrompt, loadAssignedPrompts, isTemperatureValid, onPromptConfigChange]);

  // Handle prompt deletion
  const handleDeletePrompt = React.useCallback(async () => {
    if (!currentPrompt) return;

    try {
      setIsDeleting(true);
      await deletePrompt(currentPrompt.header?.id || "");
      showToast("Prompt deleted successfully", "", "success");

      // Reload prompts first to get updated list
      await loadAssignedPrompts();

      // Find and select the active prompt after deletion
      const activePrompt = assignedPrompts?.find((p: DarwinHeader) => p.is_active);

      if (activePrompt) {
        // Load the active prompt
        await handlePromptSelection(activePrompt.id || "");
      } else {
        // If no active prompt, clear selection
        setCurrentPrompt(undefined);
        setSelectedPromptId("");
        onPromptEditorChange("");
      }
    } catch (error: any) {
      console.error("Error deleting prompt:", error);
      showToast("Failed to delete prompt", error.message || "", "error");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  }, [currentPrompt, loadAssignedPrompts, assignedPrompts, handlePromptSelection, onPromptEditorChange]);

  // Wrapper for delete handler to match generic component interface
  const handleDeletePromptWrapper = React.useCallback(() => {
    setShowDeleteConfirmation(true);
  }, []);

  // Handle prompt duplication
  const handleDuplicatePrompt = React.useCallback(async () => {
    if (!currentPrompt?.header?.id) return;

    try {
      setIsDuplicating(true);
      isDuplicatingRef.current = true;
      const duplicated = await duplicatePrompt(currentPrompt.header.id);

      if (!duplicated?.header?.id) {
        isDuplicatingRef.current = false;
        throw new Error("Failed to duplicate prompt - no ID returned");
      }

      // Reload prompts and select the new one
      await loadAssignedPrompts();

      const newPromptId = duplicated.header.id;
      setSelectedPromptId(newPromptId);

      try {
        const promptData = await getPromptByid(newPromptId);
        if (promptData) {
          const updatedPrompt = {
            header: {
              id: promptData.header?.id || "",
              name: promptData.header?.name || "",
              description: promptData.header?.description || "",
              is_active: promptData.header?.is_active || false,
              created_date: promptData.header?.created_date || 0,
              last_modified_date: promptData.header?.last_modified_date || 0,
            },
            model_config: {
              model_name: promptData.model_config?.model_name || "gpt-4o",
              model_provider: promptData.model_config?.model_provider || "openai",
              temperature: promptData.model_config?.temperature || 0,
              reasoning_effort_level: reasoningEffortLevelFromJSON(promptData.model_config?.reasoning_effort_level) ?? ReasoningEffortLevel.REASONING_NOT_VALID,
            },
            template: promptData.template || "",
          };
          setCurrentPrompt(updatedPrompt);

          onPromptEditorChange(promptData.template || "");

          if (onPromptConfigChange) {
            onPromptConfigChange("header.id", promptData.header?.id || "");
            onPromptConfigChange("header.name", promptData.header?.name || "");
            onPromptConfigChange("template", promptData.template || "");
            onPromptConfigChange("model_config.model_name", promptData.model_config?.model_name || "gpt-4o");
            onPromptConfigChange("model_config.model_provider", promptData.model_config?.model_provider || "openai");
            onPromptConfigChange("model_config.temperature", promptData.model_config?.temperature || 0);
            onPromptConfigChange("model_config.reasoning_effort_level", reasoningEffortLevelFromJSON(promptData.model_config?.reasoning_effort_level) ?? ReasoningEffortLevel.REASONING_NOT_VALID);
            onPromptConfigChange("header.is_active", promptData.header?.is_active || false);
            onPromptConfigChange("header.description", promptData.header?.description || "");
            onPromptConfigChange("header.created_date", promptData.header?.created_date || "");
            onPromptConfigChange("header.last_modified_date", promptData.header?.last_modified_date || "");
          }
        }
      } catch (e) {
        // If fetching the new prompt fails, we still updated the list; show a toast
        console.error("Error loading duplicated prompt:", e);
        showToast("Prompt duplicated but failed to load details", "", "warning");
      }

      showToast("Prompt duplicated successfully", "", "success");
    } catch (error) {
      console.error("Error duplicating prompt:", error);
      showToast("Failed to duplicate prompt", "", "error");
    } finally {
      setIsDuplicating(false);
      // Clear ref after a small delay to ensure useEffect has checked it
      setTimeout(() => {
        isDuplicatingRef.current = false;
      }, 0);
      setShowDuplicateConfirmation(false);
    }
  }, [currentPrompt, loadAssignedPrompts, onPromptEditorChange, onPromptConfigChange]);

  // Wrapper for duplicate handler to match generic component interface
  const handleDuplicatePromptWrapper = React.useCallback(() => {
    setShowDuplicateConfirmation(true);
  }, []);



  const handleOpenValidateModal = React.useCallback(async () => {
    const vars = extractVariablesFromPrompt(promptEditor);
    setVariableNames(vars);

    // If no variables detected, directly call the API
    if (!hasTemplateVariables(promptEditor)) {
      try {
        const result = await validateTemplateSyntax(promptEditor, {});
        if (result) {
          const { displayResult, isSuccess } = formatValidationResult(result, promptEditor);
          setTestResult(displayResult);
          setIsValidationSuccess(isSuccess);
          setIsTestOpen(true);
        }
      } catch (error) {
        console.error("Error validating template:", error);
        setTestResult("❌ Template validation failed. Please check your template syntax.");
        setIsValidationSuccess(false);
        setIsTestOpen(true);
      }
      return;
    }

    // If variables are present, show the variables input modal
    const initialValues: Record<string, string> = {};
    vars.forEach((v) => (initialValues[v] = variableValues[v] ?? ""));
    setVariableValues(initialValues);
    setIsVarsOpen(true);
  }, [promptEditor, variableValues]);

  const handleChangeVariableValue = (name: string, value: string) => {
    setVariableValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleRunValidateTest = async () => {
    try {
      // Convert variable values to sample data using utility function
      const sampleData = convertVariablesToSampleData(variableValues);

      const result = await validateTemplateSyntax(promptEditor, sampleData);
      if (result) {
        const { displayResult, isSuccess } = formatValidationResult(result, promptEditor);
        setTestResult(displayResult);
        setIsValidationSuccess(isSuccess);
        setIsVarsOpen(false);
        setIsTestOpen(true);
      }
    } catch (error) {
      console.error("Error validating template:", error);
      setTestResult("❌ Template validation failed. Please check your variables and template.");
      setIsValidationSuccess(false);
      setIsVarsOpen(false);
      setIsTestOpen(true);
    }
  };

  const handleGeneratePrompt = React.useCallback(() => {
    const agentType = agent?.header?.type
      ? darwinAgentTypeFromJSON(agent.header.type)
      : undefined;
    navigate("/eva/playground", {
      state: {
        initialDarwinAgentId: agent?.header?.id || "",
        initialDarwinAgentType: agentType,
        initialPromptId: selectedPromptId || currentPrompt?.header?.id || "",
        startAtStepIndex: 2,
      },
    });
  }, [navigate, agent?.header?.id, agent?.header?.type, selectedPromptId, currentPrompt?.header?.id]);


  // Modal handlers
  const onTestClose = React.useCallback(() => setIsTestOpen(false), []);
  const onVarsClose = React.useCallback(() => setIsVarsOpen(false), []);

  // Prepare props for the generic EvaRightPanel component
  const rightPanelProps = React.useMemo(() => ({
    agent,
    onPromptEditorChange,
    onPromptConfigChange,
    isCreateMode,
    defaultTimeZone,
    temperatureError,
    isPromptEditing,
    assignedPrompts,
    selectedPromptId,
    currentPrompt,
    isLoadingPrompts,
    isUpdating,
    // Event handlers
    handlePromptSelection,
    handleUpdatePrompt,
    handleOpenValidateModal,
    handleGeneratePrompt,
    setIsPromptEditing,
    setCurrentPrompt,
    setTemperatureError,
    setShowDeleteConfirmation: handleDeletePromptWrapper,
    setShowDuplicateConfirmation: handleDuplicatePromptWrapper,
    onViewDocs,
  }), [
    agent,
    onPromptEditorChange,
    onPromptConfigChange,
    isCreateMode,
    defaultTimeZone,
    temperatureError,
    isPromptEditing,
    assignedPrompts,
    selectedPromptId,
    currentPrompt,
    isLoadingPrompts,
    isUpdating,
    handlePromptSelection,
    handleUpdatePrompt,
    handleOpenValidateModal,
    handleGeneratePrompt,
    handleDeletePromptWrapper,
    handleDuplicatePromptWrapper,
    onViewDocs,
  ]);

  return (
    <>
      <EvaAgentRightPanel {...rightPanelProps} />

      {/* Delete Prompt Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeletePrompt}
        title="Delete Prompt"
        text={`Are you sure you want to delete the prompt "${currentPrompt?.header?.name || "this prompt"}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColorScheme="red"
        isLoading={isDeleting}
      />

      {/* Duplicate Prompt Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDuplicateConfirmation}
        onClose={() => setShowDuplicateConfirmation(false)}
        onConfirm={handleDuplicatePrompt}
        title="Duplicate Prompt"
        text={`Are you sure you want to duplicate the prompt "${currentPrompt?.header?.name || "this prompt"}"?`}
        confirmText="Duplicate"
        cancelText="Cancel"
        confirmColorScheme="purple"
        isLoading={isDuplicating}
      />

      {/* Template Test Result Modal */}
      <Modal isOpen={isTestOpen} onClose={onTestClose} size="xl">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader>
            {isValidationSuccess ? "Generated System Prompt" : "Template Validation Error"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            pb={6}
            overflowY="auto"
            css={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
            }}
          >
            <VStack spacing={4} align="stretch">
              <Text color="gray.600" mb={4}>
                {isValidationSuccess
                  ? "Template validated successfully"
                  : "Template validation failed"
                }
              </Text>

              <Code
                p={4}
                borderRadius="md"
                bg={isValidationSuccess ? "gray.50" : "red.50"}
                fontSize="sm"
                whiteSpace="pre-wrap"
                display="block"
              >
                {testResult}
              </Code>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Variables Input Modal for Validate & Test */}
      <Modal isOpen={isVarsOpen} onClose={onVarsClose} size="xl">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader>Enter Template Variables</ModalHeader>
          <ModalCloseButton />
          <ModalBody
            pb={6}
            overflowY="auto"
            css={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
            }}
          >
            <VStack spacing={4} align="stretch">
              <Text color="gray.600" fontSize="sm">
                Enter values for template variables. For nested variables like <Code>{"{{header.name}}"}</Code>
                Enter the value directly (e.g., "Header Name") and it will be structured as <Code>{"header: {name: \"Header Name\"}"}</Code>.
              </Text>
              {variableNames.map((name) => (
                <FormControl key={name}>
                  <FormLabel>{name}</FormLabel>
                  <Input
                    value={variableValues[name] ?? ""}
                    onChange={(e) => handleChangeVariableValue(name, e.target.value)}
                    placeholder={`Enter value for ${name}`}
                  />
                </FormControl>
              ))}
              <Flex justify="flex-end" gap={3} mt={2}>
                <Button variant="ghost" onClick={onVarsClose}>Cancel</Button>
                <Button colorScheme="blue" onClick={handleRunValidateTest}>
                  Run
                </Button>
              </Flex>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

    </>
  );
};

export default EvaAgentRightPanelWrapper;
